import { ArrowUpRight, BookOpen, GraduationCap, Check } from "lucide-react";
import { examinationPage } from "../mock";
import PageShell from "./PageShell";
import PageHero from "./PageHero";
import Reveal from "./Reveal";
import RelatedPages from "./RelatedPages";
import PullQuote from "./PullQuote";
import SectionOrnament from "./SectionOrnament";

export default function ExaminationPage() {
  const { hero, intro, classes, highlights } = examinationPage;

  return (
    <PageShell>
      {({ openEnquire }) => (
        <>
          <PageHero eyebrow={hero.eyebrow} title={hero.title} italic={hero.italic} image={hero.image} blurb={hero.blurb} />

          {/* Intro */}
          <section className="bg-white py-14 md:py-20">
            <div className="max-w-[900px] mx-auto px-5 md:px-10 text-center">
              <Reveal>
                <BookOpen size={26} className="text-[var(--gold)] mx-auto" strokeWidth={1.4} />
                <span className="gold-divider-thin block mx-auto my-5" />
                <p className="text-[16px] md:text-[18px] leading-[1.85] text-[var(--ink)]/80 serif">
                  {intro}
                </p>
              </Reveal>
              <SectionOrnament />
            </div>
          </section>

          {/* Class-wise details */}
          <section className="bg-[var(--cream)] py-14 md:py-20">
            <div className="max-w-[1200px] mx-auto px-5 md:px-10">
              <Reveal>
                <div className="text-center max-w-[760px] mx-auto mb-10 md:mb-14">
                  <div className="eyebrow mb-4">Class-Wise Evaluation</div>
                  <h2 className="h-section text-[var(--ink)]">
                    A gentle, skill-based
                    <span className="serif-italic gold-text"> approach.</span>
                  </h2>
                </div>
              </Reveal>
              <div className="space-y-5 md:space-y-6">
                {classes.map((c, i) => (
                  <Reveal key={c.label} delay={i * 80}>
                    <article className="grid grid-cols-1 md:grid-cols-12 gap-4 md:gap-8 p-6 md:p-8 bg-white border border-[rgba(184,148,86,0.25)] light-card">
                      <div className="md:col-span-4 flex md:flex-col items-start gap-3 md:gap-2">
                        <GraduationCap size={20} className="text-[var(--gold)] mt-1" strokeWidth={1.4} />
                        <div>
                          <h3 className="serif text-[24px] md:text-[30px] text-[var(--ink)] leading-tight">
                            {c.label}
                          </h3>
                          <div className="serif-italic gold-text text-[15px] md:text-[17px] mt-0.5">
                            {c.tagline}
                          </div>
                        </div>
                      </div>
                      <div className="md:col-span-8">
                        <p className="text-[14.5px] md:text-[15.5px] leading-[1.8] text-[var(--ink)]/80">
                          {c.body}
                        </p>
                      </div>
                    </article>
                  </Reveal>
                ))}
              </div>
            </div>
          </section>

          <PullQuote
            tone="dark"
            text="Assessment is not about marks. It is about understanding what a child has truly learned — and what we can do better."
          />

          {/* Highlights */}
          <section className="relative py-14 md:py-20 overflow-hidden bg-white">
            <div className="relative max-w-[1100px] mx-auto px-5 md:px-10">
              <Reveal>
                <div className="text-center max-w-[700px] mx-auto mb-10 md:mb-14">
                  <div className="eyebrow mb-4">Highlights</div>
                  <h2 className="h-section text-[var(--ink)]">
                    Built on the
                    <span className="block serif-italic gold-text">CBSE framework.</span>
                  </h2>
                </div>
              </Reveal>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-x-10 gap-y-5">
                {highlights.map((h, i) => (
                  <Reveal key={h} delay={(i % 2) * 100}>
                    <div className="flex items-start gap-3 border-b border-[rgba(184,148,86,0.3)] pb-4">
                      <Check size={18} className="text-[var(--gold)] mt-1 shrink-0" strokeWidth={1.5} />
                      <span className="serif text-[var(--ink)] text-[16px] md:text-[17.5px] leading-snug">{h}</span>
                    </div>
                  </Reveal>
                ))}
              </div>
              <Reveal>
                <div className="mt-12 md:mt-14 text-center">
                  <button onClick={openEnquire} className="btn-gold inline-flex items-center gap-2" style={{ background: "var(--gold)", color: "var(--navy-deep)" }}>
                    Begin Enquiry <ArrowUpRight size={16} />
                  </button>
                </div>
              </Reveal>
            </div>
          </section>

          <RelatedPages current="/examination.html" />
        </>
      )}
    </PageShell>
  );
}
